/**
 * Package loading.
 *
 * Phase 1 reads packages straight from `server/resources/jtpk/packages/` so the
 * engine can be exercised against the reference package before the install
 * endpoint exists. Phase 2 replaces this with the stored, validated package the
 * import pipeline writes — the shape of `TemplatePackage` does not change.
 */

import type { LocaleStrings, TemplatePackage, TemplateSection } from "./types";

const PACKAGE_FILES = import.meta.glob("../../../../server/resources/jtpk/packages/**/*.json", {
  eager: true,
  import: "default",
}) as Record<string, unknown>;

const PACKAGE_ROOT = "server/resources/jtpk/packages/";

function relativePath(absolutePath: string): { key: string; file: string } | null {
  const index = absolutePath.indexOf(PACKAGE_ROOT);
  if (index === -1) return null;
  const rest = absolutePath.slice(index + PACKAGE_ROOT.length);
  const slash = rest.indexOf("/");
  if (slash === -1) return null;
  return { key: rest.slice(0, slash), file: rest.slice(slash + 1) };
}

/** Flatten a nested locale tree to the dotted keys `resolveValue` looks up. */
export function flattenStrings(tree: LocaleStrings, prefix = ""): Record<string, string> {
  const flat: Record<string, string> = {};
  for (const [key, value] of Object.entries(tree)) {
    const path = `${prefix}${key}`;
    if (value && typeof value === "object") {
      Object.assign(flat, flattenStrings(value as LocaleStrings, `${path}.`));
    } else {
      flat[path] = String(value);
    }
  }
  return flat;
}

export function loadPackage(key: string): TemplatePackage {
  const files: Record<string, unknown> = {};
  for (const [absolutePath, contents] of Object.entries(PACKAGE_FILES)) {
    const parsed = relativePath(absolutePath);
    if (parsed?.key === key) files[parsed.file] = contents;
  }

  const manifest = files["template.json"];
  if (!manifest) throw new Error(`Template package not found: ${key}`);

  const layout = files["layout.json"] as TemplatePackage["layout"];
  const sections: TemplateSection[] = layout.sections.map((entry) => {
    if ("$ref" in entry) {
      const target = files[entry.$ref];
      if (!target) throw new Error(`Package ${key}: section not found: ${entry.$ref}`);
      return target as TemplateSection;
    }
    return entry;
  });

  const i18n: Record<string, LocaleStrings> = {};
  for (const [file, contents] of Object.entries(files)) {
    const match = file.match(/^i18n\/([a-z]{2})\.json$/);
    if (match) i18n[match[1]] = contents as LocaleStrings;
  }

  return {
    manifest: manifest as TemplatePackage["manifest"],
    tokens: files["tokens.json"] as TemplatePackage["tokens"],
    layout,
    sections,
    i18n,
  };
}

/** Template strings for a locale, flattened, with a fallback to the other locale. */
export function stringsForLocale(pkg: TemplatePackage, locale: string): Record<string, string> {
  const primary = pkg.i18n[locale] ?? pkg.i18n.ro ?? {};
  const fallback = pkg.i18n[locale === "ro" ? "en" : "ro"] ?? {};
  return { ...flattenStrings(fallback), ...flattenStrings(primary) };
}

export function availablePackages(): string[] {
  const keys = new Set<string>();
  for (const absolutePath of Object.keys(PACKAGE_FILES)) {
    const parsed = relativePath(absolutePath);
    if (parsed?.file === "template.json") keys.add(parsed.key);
  }
  return [...keys].sort();
}
